import { useState, useEffect } from "react";
import Icon from "../Common/Icon";

export default function DevNoteModal({ isOpen, onClose, onSubmit, initialData }) {
  const [noteType, setNoteType] = useState("VERSION");
  const [version, setVersion] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [status, setStatus] = useState("PLANLANAN");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [badgeText, setBadgeText] = useState("");

  useEffect(() => {
    if (initialData) {
      setNoteType(initialData.noteType ?? "VERSION");
      setVersion(initialData.version ?? "");
      setReleaseDate(initialData.releaseDate ?? "");
      setStatus(initialData.status ?? "PLANLANAN");
      setTitle(initialData.title ?? "");
      setDescription(initialData.description ?? "");
      setBadgeText(initialData.badgeText ?? "");
    } else {
      setNoteType("VERSION");
      setVersion("");
      setReleaseDate("");
      setStatus("PLANLANAN");
      setTitle("");
      setDescription("");
      setBadgeText("");
    }
  }, [initialData, isOpen]);

  // Adjust status defaults when noteType changes
  const handleTypeChange = (type) => {
    setNoteType(type);
    if (type === "VERSION") {
      setStatus("PLANLANAN");
    } else {
      setStatus("IN_PROGRESS");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      noteType,
      version: noteType === "VERSION" ? version : null,
      releaseDate: noteType === "VERSION" ? releaseDate : null,
      status,
      title,
      description,
      badgeText: noteType === "ROADMAP" ? badgeText : null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-slide-in">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
          <div className="flex items-center gap-2">
            <Icon name="construction" className="text-primary !text-[24px]" />
            <h3 className="font-bold text-lg text-on-surface">
              {initialData ? "Notu Güncelle" : "Yeni Geliştirme Notu"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-on-surface-variant hover:text-primary transition"
          >
            <Icon name="close" className="!text-[22px]" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
            {/* Note Type */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Not Tipi</label>
              <select
                value={noteType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
              >
                <option value="VERSION">Versiyon Notu (Release Version)</option>
                <option value="ROADMAP">Yol Haritası / Gelecek Özellik (Roadmap)</option>
              </select>
            </div>

            {/* Note Type: VERSION Specific Fields */}
            {noteType === "VERSION" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Versiyon No (Örn: v1.0.0)</label>
                  <input
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    required
                    placeholder="v1.0.0"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Yayınlanma Tarihi (Örn: 10 Temmuz 2026)</label>
                  <input
                    type="text"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    required
                    placeholder="Yakında veya Tarih"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
              </div>
            )}

            {/* Status Select */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Durum</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
              >
                {noteType === "VERSION" ? (
                  <>
                    <option value="KARARLI">KARARLI (Stable)</option>
                    <option value="PLANLANAN">PLANLANAN (Planned)</option>
                  </>
                ) : (
                  <>
                    <option value="IN_PROGRESS">Geliştiriliyor (In Progress)</option>
                    <option value="PLANNED">Planlananlar (Planned)</option>
                    <option value="BACKLOG">Backlog & Fikirler (Backlog)</option>
                  </>
                )}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlık</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Özellik Başlığı"
                className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Not açıklaması veya yenilik detayı..."
                className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
              />
            </div>

            {/* Note Type: ROADMAP Specific Badge */}
            {noteType === "ROADMAP" && (
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Rozet Metni (Örn: v1.1.0, Önemli, Ar-Ge)</label>
                <input
                  type="text"
                  value={badgeText}
                  onChange={(e) => setBadgeText(e.target.value)}
                  placeholder="Seçenek: v1.1.0, Önemli, Ar-Ge"
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-outline-variant bg-surface-container-low flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs"
            >
              İptal
            </button>
            <button
              type="submit"
              className="bg-primary text-white hover:opacity-90 transition rounded-xl px-4 py-2 font-semibold text-xs"
            >
              {initialData ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
